import React, { useEffect, useRef } from "react";

const LONG_PRESS_MS = 450;
const HINT_MS = 220;

export const HIGHLIGHT_COLORS = {
    sage:  "rgba(183,205,177,0.55)",
    amber: "rgba(231,195,111,0.55)",
    plum:  "rgba(199,160,176,0.55)",
    slate: "rgba(156,176,192,0.55)",
    clay:  "rgba(216,180,158,0.55)",
};

export default function Reader({
    chapter,
    verses,
    version = "kjv",
    onTapVerse,
    activeVerse,
    showFooter = true,
    highlightsByVerse = {},
    selectionActive = false,
    selectedVerses = [],
    onLongPressVerse,
    onToggleSelect,
    questionsByVerse = {},
    showQuestions = true,
    onOpenQuestion,
}) {
    const containerRef = useRef(null);

    useEffect(() => {
        containerRef.current?.scrollTo({ top: 0, behavior: "instant" });
    }, [chapter]);

    return (
        <div
            ref={containerRef}
            className="px-6 md:px-10 lg:px-6 xl:px-12 py-8 md:py-12 pb-32 mx-auto w-full"
            style={{ maxWidth: "68ch" }}
        >
            <div className="mb-8 md:mb-10">
                <div className="font-ui text-[11px] uppercase tracking-[0.25em] text-moss-deep">
                    The Gospel According to
                </div>
                <h1 className="scripture text-5xl md:text-6xl font-medium text-ink mt-1 tracking-tight">
                    Matthew
                </h1>
                <div className="mt-2 font-ui text-sm text-ink-40">Chapter {chapter}</div>
            </div>

            <p
                data-testid={`chapter-body-${chapter}`}
                className="scripture text-[19px] md:text-[20px] leading-[1.85] md:leading-[1.9] text-ink"
            >
                {verses.map((v, i) => {
                    const text = version === "web" ? (v.text_web || v.text_kjv) : (v.text_kjv || v.text_web);
                    const hasQ = showQuestions && questionsByVerse[v.verse] > 0;
                    return (
                        <React.Fragment key={v.verse}>
                            <VerseSpan
                                chapter={chapter}
                                verse={v.verse}
                                text={text}
                                isActive={activeVerse === v.verse}
                                isSelected={selectionActive && selectedVerses.includes(v.verse)}
                                selectionMode={selectionActive}
                                highlightColor={highlightsByVerse[v.verse]}
                                onLongPress={onLongPressVerse}
                                onToggleSelect={onToggleSelect}
                                onTap={onTapVerse}
                            />
                            {hasQ && !selectionActive && (
                                <button
                                    data-testid={`question-icon-${v.verse}`}
                                    onClick={(e) => { e.stopPropagation(); onOpenQuestion?.(v.verse); }}
                                    aria-label={`Questions on verse ${v.verse}`}
                                    title="Questions on this verse"
                                    className="inline-flex items-center justify-center align-baseline mx-0.5 -translate-y-[3px] w-[18px] h-[18px] md:w-[19px] md:h-[19px] rounded-full bg-moss-deep text-white font-ui text-[11px] font-bold leading-none shadow-[0_1px_2px_rgba(30,43,38,0.15)] ring-1 ring-moss-deep/20 hover:bg-moss hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-moss/60 transition cursor-pointer select-none"
                                >
                                    ?
                                </button>
                            )}
                            {i < verses.length - 1 && " "}
                        </React.Fragment>
                    );
                })}
            </p>

            {showFooter && (
                <footer className="mt-16 pt-6 border-t border-ink-10">
                    <p className="font-ui text-[11px] leading-relaxed text-ink-40">
                        Scripture: King James Version (public domain). Lexical data from{" "}
                        <a href="https://stepbible.org" target="_blank" rel="noreferrer noopener"
                           className="underline decoration-ink-10 hover:decoration-moss transition">
                            STEPBible.org
                        </a>, Tyndale House Cambridge, CC BY 4.0. World English Bible (public domain) also available.
                    </p>
                </footer>
            )}
        </div>
    );
}

function VerseSpan({ chapter, verse, text, isActive, isSelected, selectionMode, highlightColor, onLongPress, onToggleSelect, onTap }) {
    const timerRef = useRef(null);
    const hintTimerRef = useRef(null);
    const startedAt = useRef(0);
    const moved = useRef(false);
    const longPressFired = useRef(false);
    const suppressNextClick = useRef(false);
    const [pressing, setPressing] = React.useState(false);

    const cancel = () => {
        clearTimeout(timerRef.current);
        clearTimeout(hintTimerRef.current);
        timerRef.current = null;
        hintTimerRef.current = null;
        setPressing(false);
    };

    const onPointerDown = (e) => {
        longPressFired.current = false;
        if (selectionMode) return; // taps toggle in selection mode
        if (e.pointerType === "mouse" && e.button !== 0) return;
        moved.current = false;
        startedAt.current = Date.now();
        const px = e.clientX, py = e.clientY;
        hintTimerRef.current = setTimeout(() => setPressing(true), HINT_MS);
        timerRef.current = setTimeout(() => {
            cancel();
            longPressFired.current = true;
            suppressNextClick.current = true;
            onLongPress?.(verse, { x: px, y: py });
        }, LONG_PRESS_MS);
    };
    const onPointerMove = () => { moved.current = true; };
    const onPointerUp = () => { cancel(); };
    const onPointerLeave = () => { cancel(); };

    const handleClick = (e) => {
        // The click that follows a long-press must not toggle the just-selected verse
        if (suppressNextClick.current) {
            suppressNextClick.current = false;
            e.preventDefault(); e.stopPropagation();
            return;
        }
        if (selectionMode) {
            e.preventDefault(); e.stopPropagation();
            onToggleSelect?.(verse);
            return;
        }
        if (Date.now() - startedAt.current < LONG_PRESS_MS && !moved.current) {
            onTap?.(verse);
        }
    };

    const style = highlightColor ? { background: HIGHLIGHT_COLORS[highlightColor] || undefined, borderRadius: 4, padding: "0 2px" } : undefined;

    let className = "cursor-pointer transition-colors duration-200 -mx-0.5 px-0.5 rounded-md";
    if (isSelected) className += " ring-2 ring-moss/50 bg-moss/10";
    else if (isActive) className += " bg-moss/15 verse-pulse";
    else if (pressing) className += " bg-moss/10";
    else className += " hover:bg-moss/10";

    return (
        <span
            data-testid={`verse-${verse}`}
            data-selected={isSelected || undefined}
            data-highlight={highlightColor || undefined}
            style={style}
            className={className}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerLeave}
            onClick={handleClick}
            onContextMenu={(e) => { e.preventDefault(); onLongPress?.(verse, { x: e.clientX, y: e.clientY }); }}
        >
            <sup className="font-ui text-[10px] font-medium text-moss-deep/70 mr-1 select-none">
                {verse}
            </sup>
            {text}
        </span>
    );
}
